// /plan/guidance-profiles — Module 6 library. Mirrors /plan/airframes:
// scope filter (preset/mine/all), preset rows with clone action, tenant
// rows with delete, mode + seeker chips.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { formatApiError } from "@/lib/formatApiError";
import {
    Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import SpinnerButton from "@/components/SpinnerButton";
import PresetPublishButton from "@/components/PresetPublishButton";
import GuidanceProfileEditor from "@/components/guidance/GuidanceProfileEditor";
import { sim } from "@/api/endpoints";
import { useAuth } from "@/auth/AuthContext";
import { presetImmutabilityToast } from "@/lib/presetImmutability";

const SCOPES = [
    { id: "tenant", label: "All sources" },
    { id: "mine", label: "Mine" },
    { id: "preset", label: "Presets" },
];

const MODE_LABELS = {
    pure_pursuit: "Pure Pursuit",
    pn: "PN",
    apn: "APN",
    clos: "CLOS",
};

function param(v) {
    return typeof v === "number" ? v : (v && typeof v === "object" ? v.value : null);
}

export default function GuidanceProfilesPage() {
    const [items, setItems] = useState([]);
    const [scope, setScope] = useState("tenant");
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [busyId, setBusyId] = useState(null);
    const [editTarget, setEditTarget] = useState(null);
    const [editDraft, setEditDraft] = useState({});
    const [editSaving, setEditSaving] = useState(false);
    const { user } = useAuth();

    const load = useCallback(async () => {
        setLoading(true);
        setErr(null);
        try {
            const list = await sim.guidanceProfiles.list({ scope });
            setItems(Array.isArray(list) ? list : []);
        } catch (e) {
            setErr(formatApiError(e, "Load failed"));
        } finally {
            setLoading(false);
        }
    }, [scope]);

    useEffect(() => {
        load();
    }, [load]);

    const onClone = async (id) => {
        setBusyId(id);
        try {
            const cloned = await sim.guidanceProfiles.clone(id);
            toast.success(`Cloned as "${cloned.name}"`);
            await load();
        } catch (e) {
            toast.error(formatApiError(e, "Clone failed"));
        } finally {
            setBusyId(null);
        }
    };

    const onDelete = async (id) => {
        if (!window.confirm("Delete this guidance profile?")) return;
        setBusyId(id);
        try {
            await sim.guidanceProfiles.remove(id);
            toast.success("Profile deleted");
            await load();
        } catch (e) {
            presetImmutabilityToast(e, {
                entityType: "guidance-profiles",
                id,
                onCloned: async () => { await load(); },
            });
        } finally {
            setBusyId(null);
        }
    };

    // Phase 6 — open the dedicated GuidanceProfileEditor modal.
    const openEdit = (p) => {
        setEditTarget(p);
        setEditDraft({});
    };

    const onSaveEdit = async () => {
        if (!editTarget) return;
        if (!Object.keys(editDraft).length) {
            setEditTarget(null);
            return;
        }
        setEditSaving(true);
        try {
            await sim.guidanceProfiles.patch(editTarget.id, editDraft);
            toast.success("Profile updated");
            await load();
            setEditTarget(null);
            setEditDraft({});
        } catch (e) {
            presetImmutabilityToast(e, {
                entityType: "guidance-profiles",
                id: editTarget.id,
                onCloned: async () => {
                    await load();
                },
            });
        } finally {
            setEditSaving(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="guidance-profiles-page">
            <PageHeader
                title="Guidance profiles"
                subtitle="Interceptor guidance laws + seeker + lock-loss policies."
                actions={
                    <Select value={scope} onValueChange={setScope}>
                        <SelectTrigger
                            className="w-[170px] bg-ux-bg-2 text-[12px] font-mono"
                            data-testid="guidance-profiles-scope"
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-ux-bg-1 border-ux-border-1">
                            {SCOPES.map((s) => (
                                <SelectItem
                                    key={s.id}
                                    value={s.id}
                                    className="text-[12px] font-mono"
                                >
                                    {s.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                }
            />
            <div className="border border-ux-border-1 rounded-md bg-ux-bg-1 p-3">
                {loading ? (
                    <div className="text-xs text-ux-text-3">Loading…</div>
                ) : err ? (
                    <div className="text-xs text-ux-danger" data-testid="guidance-profiles-error">
                        {err}
                    </div>
                ) : items.length === 0 ? (
                    <EmptyState
                        title="No guidance profiles yet"
                        description="Create a profile from the Workbench, or clone a preset."
                    />
                ) : (
                    <ul className="space-y-2" data-testid="guidance-profiles-list">
                        {items.map((p) => {
                            const accel = param(p.max_lateral_accel_g);
                            return (
                                <li
                                    key={p.id}
                                    className="flex items-start gap-3 px-3 py-2 rounded-md border border-ux-border-1 hover:border-ux-border-2"
                                    data-testid={`guidance-row-${p.id}`}
                                >
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 text-sm">
                                            <span className="font-medium">{p.name}</span>
                                            <span className="text-[10px] font-mono uppercase tracking-wider text-ux-text-3 border border-ux-border-1 rounded-full px-2 py-0.5">
                                                {MODE_LABELS[p.mode] || p.mode}
                                            </span>
                                            <span className="text-[10px] font-mono uppercase tracking-wider text-ux-text-3 border border-ux-border-1 rounded-full px-2 py-0.5">
                                                {(p.seeker_type || "").toUpperCase()}
                                            </span>
                                            {p.is_preset ? (
                                                <span className="text-[10px] font-mono uppercase tracking-wider text-ux-text-3">
                                                    preset
                                                </span>
                                            ) : null}
                                        </div>
                                        {p.notes ? (
                                            <div className="text-[12px] text-ux-text-3 mt-1">{p.notes}</div>
                                        ) : null}
                                        <div className="text-[11px] font-mono text-ux-text-3 mt-1">
                                            Max accel {accel != null ? `${accel.toFixed(1)}g` : "—"}
                                            {" · Reacquire "}
                                            {param(p?.lock_loss?.reacquire_timeout_s) != null
                                                ? `${param(p.lock_loss.reacquire_timeout_s)}s`
                                                : "—"}
                                        </div>
                                    </div>
                                    {p.is_preset ? (
                                        <SpinnerButton
                                            size="sm"
                                            variant="ghost"
                                            loading={busyId === p.id}
                                            onClick={() => onClone(p.id)}
                                            testId={`guidance-clone-${p.id}`}
                                            title="Clone to my tenant"
                                            aria-label="Clone to my tenant"
                                        >
                                            <Copy className="h-3.5 w-3.5" />
                                        </SpinnerButton>
                                    ) : (
                                        <>
                                            <button
                                                type="button"
                                                onClick={() => openEdit(p)}
                                                className="text-ux-text-3 hover:text-ux-accent"
                                                data-testid={`guidance-edit-${p.id}`}
                                                aria-label="Edit metadata"
                                                title="Edit metadata"
                                            >
                                                <Pencil className="h-3.5 w-3.5" />
                                            </button>
                                            <PresetPublishButton
                                                entityType="guidance-profiles"
                                                entityId={p.id}
                                                entity={p}
                                                user={user}
                                                size="sm"
                                                variant="ghost"
                                                label=""
                                                testId={`guidance-publish-${p.id}`}
                                                onPublished={load}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => onDelete(p.id)}
                                                className="text-ux-text-3 hover:text-ux-danger"
                                                data-testid={`guidance-delete-${p.id}`}
                                                aria-label="Delete"
                                                title="Delete"
                                            >
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </button>
                                        </>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
            <div className="text-[11px] text-ux-text-3">
                <Link to="/plan/threats" className="text-ux-accent underline decoration-dotted">← Threats</Link>
            </div>

            <Dialog
                open={!!editTarget}
                onOpenChange={(o) => {
                    if (!o) {
                        setEditTarget(null);
                        setEditDraft({});
                    }
                }}
            >
                <DialogContent
                    className="max-w-2xl"
                    data-testid="guidance-edit-dialog"
                >
                    <DialogHeader>
                        <DialogTitle>
                            Edit guidance profile
                            {editTarget?.name ? ` — ${editTarget.name}` : ""}
                        </DialogTitle>
                    </DialogHeader>
                    {editTarget ? (
                        <GuidanceProfileEditor
                            value={{ ...editTarget, ...editDraft }}
                            onChange={(patch) =>
                                setEditDraft((d) => ({ ...d, ...patch }))
                            }
                            testId="guidance-profile-editor-instance"
                        />
                    ) : null}
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                setEditTarget(null);
                                setEditDraft({});
                            }}
                            disabled={editSaving}
                            data-testid="guidance-edit-cancel"
                        >
                            Cancel
                        </Button>
                        <SpinnerButton
                            size="sm"
                            loading={editSaving}
                            onClick={onSaveEdit}
                            testId="guidance-edit-save"
                        >
                            Save changes
                        </SpinnerButton>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
